import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import openai from '../../../services/openaiClient';

const PropertyExpirationActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('expiring');
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [renewalPeriod, setRenewalPeriod] = useState('30');

  const handleAIRenewalStrategy = async () => {
    setIsLoadingAI(true);
    try {
      const completion = await openai?.chat?.completions?.create({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: 'أنت مساعد ذكي متخصص في إدارة العقارات وتجديد الإعلانات. قدم استراتيجيات فعالة للتجديد والحفاظ على العقارات نشطة.'
          },
          {
            role: 'user',
            content: aiQuery || 'ما هي أفضل استراتيجية لتجديد إعلانات العقارات المنتهية؟'
          }
        ],
        temperature: 0.7,
        max_tokens: 500
      });
      
      setAiResponse(completion?.choices?.[0]?.message?.content || 'لم يتم الحصول على رد');
    } catch (error) {
      console.error('AI Analysis Error:', error);
      setAiResponse('حدث خطأ في تحليل الذكاء الاصطناعي. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoadingAI(false);
    }
  };

  const handleRenewProperty = () => {
    if (!selectedProperty || !renewalPeriod) {
      alert('يرجى اختيار العقار وفترة التجديد');
      return;
    }
    console.log('Renewing property:', { selectedProperty, renewalPeriod });
    alert('تم تجديد العقار بنجاح');
  };

  const tabs = [
    { id: 'expiring', label: 'العقارات المنتهية', icon: 'AlertCircle' },
    { id: 'renewal', label: 'التجديد السريع', icon: 'RefreshCw' },
    { id: 'ai-strategy', label: 'استراتيجية التجديد', icon: 'Sparkles' }
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="انتهاء العقارات"
      icon="Building2"
      iconColor="#EF4444"
      size="xl"
      footer={null}
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-border">
        {tabs?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              activeTab === tab?.id
                ? 'border-primary text-primary font-semibold' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={18} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'expiring' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">العقارات المنتهية والقريبة من الانتهاء</h3>
            
            <div className="space-y-3">
              <div className="p-4 border-l-4 border-red-500 bg-red-50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-red-900">PROP-2026-015</h4>
                    <p className="text-sm text-red-700">مكتب تجاري في الشميساني</p>
                  </div>
                  <span className="px-2 py-1 bg-red-200 text-red-800 text-xs rounded font-semibold">منتهي</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-red-700">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    انتهى: ٢٠٢٦/٠١/٠٨
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    محمد خالد
                  </span>
                </div>
                <Button size="sm" className="mt-2" variant="outline">
                  <span className="flex items-center gap-1">
                    <Icon name="RefreshCw" size={14} />
                    تجديد الآن
                  </span>
                </Button>
              </div>

              <div className="p-4 border-l-4 border-orange-500 bg-orange-50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-orange-900">PROP-2026-022</h4>
                    <p className="text-sm text-orange-700">شقة في خلدا</p>
                  </div>
                  <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs rounded font-semibold">3 أيام</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-orange-700">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    ينتهي: ٢٠٢٦/٠١/١٣
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    سارة أحمد
                  </span>
                </div>
                <Button size="sm" className="mt-2" variant="outline">
                  <span className="flex items-center gap-1">
                    <Icon name="Phone" size={14} />
                    اتصل بالمالك
                  </span>
                </Button>
              </div>

              <div className="p-4 border-l-4 border-yellow-500 bg-yellow-50 rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-yellow-900">PROP-2026-018</h4>
                    <p className="text-sm text-yellow-700">فيلا في الريم</p>
                  </div>
                  <span className="px-2 py-1 bg-yellow-200 text-yellow-800 text-xs rounded font-semibold">7 أيام</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-yellow-700">
                  <span className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    ينتهي: ٢٠٢٦/٠١/١٧
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="User" size={14} />
                    أحمد محمد
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">8</p>
                <p className="text-xs text-red-700">منتهي</p>
              </div>
              <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg text-center">
                <p className="text-2xl font-bold text-orange-600">15</p>
                <p className="text-xs text-orange-700">خلال 3 أيام</p>
              </div>
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-600">27</p>
                <p className="text-xs text-yellow-700">خلال أسبوع</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'renewal' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">تجديد سريع للعقار</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">اختر العقار</label>
                <select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                >
                  <option value="">-- اختر عقار --</option>
                  <option value="prop-1">PROP-2026-015 - مكتب تجاري في الشميساني</option>
                  <option value="prop-2">PROP-2026-022 - شقة في خلدا</option>
                  <option value="prop-3">PROP-2026-018 - فيلا في الريم</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">فترة التجديد</label>
                <select
                  value={renewalPeriod}
                  onChange={(e) => setRenewalPeriod(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background"
                >
                  <option value="30">30 يوم</option>
                  <option value="60">60 يوم</option>
                  <option value="90">90 يوم</option>
                  <option value="180">6 أشهر</option>
                  <option value="365">سنة</option>
                </select>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">خيارات التجديد</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                    <span className="text-sm">تحديث الصور والوصف</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" defaultChecked className="w-4 h-4" />
                    <span className="text-sm">مراجعة السعر</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="w-4 h-4" />
                    <span className="text-sm">ترقية إلى إعلان مميز</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" className="w-4 h-4" />
                    <span className="text-sm">إضافة جولة افتراضية</span>
                  </label>
                </div>
              </div>

              <Button
                onClick={handleRenewProperty}
                className="w-full"
                variant="primary"
              >
                <span className="flex items-center gap-2">
                  <Icon name="RefreshCw" size={18} />
                  تجديد العقار
                </span>
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'ai-strategy' && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-foreground mb-4">استراتيجية التجديد الذكية</h3>
            
            <div className="space-y-3">
              <label className="block text-sm font-medium">اسأل عن استراتيجية التجديد</label>
              <Input
                value={aiQuery}
                onChange={(e) => setAiQuery(e?.target?.value)}
                placeholder="مثال: كيف أحسن معدل تجديد العقارات؟"
                className="w-full"
              />
              <Button
                onClick={handleAIRenewalStrategy}
                disabled={isLoadingAI}
                className="w-full"
              >
                {isLoadingAI ? (
                  <span className="flex items-center gap-2">
                    <Icon name="Loader" size={18} className="animate-spin" />
                    جاري التحليل...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Icon name="Sparkles" size={18} />
                    احصل على استراتيجية
                  </span>
                )}
              </Button>
            </div>

            {aiResponse && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2 mb-2">
                  <Icon name="Lightbulb" size={20} className="text-blue-600 mt-1" />
                  <h4 className="font-semibold text-blue-900">توصيات الذكاء الاصطناعي</h4>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiResponse}</p>
              </div>
            )}

            <div className="mt-6 p-4 bg-muted/30 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Icon name="TrendingUp" size={18} className="text-green-600" />
                نصائح لتحسين معدل التجديد
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5" />
                  <span>تواصل مع المالك قبل 7 أيام من الانتهاء</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5" />
                  <span>قدم تقرير أداء العقار خلال الفترة السابقة</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5" />
                  <span>اقترح تحسينات على الإعلان</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="Check" size={16} className="text-green-600 mt-0.5" />
                  <span>قدم عروض خاصة للتجديد المبكر</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PropertyExpirationActionsModal;